import { useEffect, useLayoutEffect, useRef, type ComponentProps, type ReactNode, type Ref } from 'react';
import { cx } from '../utils.ts';
import './form.css';

export interface InputProps extends Omit<ComponentProps<'input'>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  /** Leading adornment (icon, prefix text). */
  leading?: ReactNode;
  /** Trailing adornment (unit, clear button, kbd). */
  trailing?: ReactNode;
  invalid?: boolean;
  /** Borderless look for inline editing (table cells, titles). */
  variant?: 'default' | 'ghost';
  wrapperClassName?: string;
}

export function Input({ size = 'md', leading, trailing, invalid, variant = 'default', className, wrapperClassName, ...rest }: InputProps) {
  return (
    <div className={cx('g-input', wrapperClassName)} data-size={size} data-variant={variant} data-invalid={invalid || undefined} data-disabled={rest.disabled || undefined}>
      {leading && <span className="g-input__adorn g-input__adorn--lead">{leading}</span>}
      <input className={cx('g-input__control', className)} aria-invalid={invalid || undefined} {...rest} />
      {trailing && <span className="g-input__adorn g-input__adorn--trail">{trailing}</span>}
    </div>
  );
}

export interface TextareaProps extends ComponentProps<'textarea'> {
  invalid?: boolean;
  /** Grow with content up to `maxRows`. */
  autoGrow?: boolean;
  maxRows?: number;
  variant?: 'default' | 'ghost';
}

export function Textarea({ invalid, autoGrow, maxRows = 12, variant = 'default', className, ref, onInput, ...rest }: TextareaProps) {
  const inner = useRef<HTMLTextAreaElement | null>(null);
  const setRef = (node: HTMLTextAreaElement | null) => {
    inner.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = node;
  };
  const resize = () => {
    const el = inner.current;
    if (!el || !autoGrow) return;
    el.style.height = 'auto';
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${Math.min(el.scrollHeight + 2, line * maxRows + 20)}px`;
  };
  useLayoutEffect(resize);
  // Re-measure when the width changes (window resize, sidebar toggling).
  useEffect(() => {
    const el = inner.current;
    if (!el || !autoGrow || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return; // our own height changes land here too
      width = el.clientWidth;
      resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGrow]);
  return (
    <textarea
      ref={setRef as Ref<HTMLTextAreaElement>}
      className={cx('g-textarea', className)}
      data-variant={variant}
      aria-invalid={invalid || undefined}
      onInput={(e) => {
        resize();
        onInput?.(e);
      }}
      {...rest}
    />
  );
}

export interface FieldProps {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** Right side of the label row (e.g. a link). */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  required?: boolean;
}

export function Field({ label, htmlFor, hint, error, aside, children, className, required }: FieldProps) {
  return (
    <div className={cx('g-field', className)}>
      {(label || aside) && (
        <div className="g-field__top">
          {label && (
            <label className="g-field__label" htmlFor={htmlFor}>
              {label}
              {required && <span className="g-field__req">*</span>}
            </label>
          )}
          {aside && <span className="g-field__aside">{aside}</span>}
        </div>
      )}
      {children}
      {error ? <p className="g-field__error">{error}</p> : hint ? <p className="g-field__hint">{hint}</p> : null}
    </div>
  );
}
